import {
  ChangeDetectorRef,
  Component,
  EventEmitter,
  Input,
  OnChanges,
  OnInit,
  Output,
  SimpleChanges,
} from '@angular/core';
import { FormControl } from '@angular/forms';
import { LearningResourceViewModel } from '@app/inputs/models/learning-resource.view-model';
import { OrgEndorsedService } from '@app/orgs/services/org-endorsed.service';
import { SearchFilterFilter } from '@app/search/search-api.model';
import { SearchService } from '@app/search/services/search.service';
import { SubscriberBaseDirective } from '@app/shared/components/subscriber-base/subscriber-base.directive';
import { AuthService } from '@app/shared/services/auth.service';
import { LDFlagsService } from '@app/shared/services/ld-flags.service';
import { debounceTime, distinctUntilChanged, filter } from 'rxjs/operators';
import { OrgManagedContentService } from '../org-managed-notification/org-managed-content.service';

interface ModalResults<T> {
  savedItems?: T[];
  selectedNodes?: string[];
  isBin?: boolean;
  whichModal?: string;
  selectedManual?: T[];
  selectedSearch?: T[];
}

@Component({
  selector: 'dgx-add-by-search-base',
  templateUrl: './add-by-search-base.component.html',
})
export class AddBySearchBaseComponent
  extends SubscriberBaseDirective
  implements OnInit, OnChanges
{
  public static readonly modalName = 'search';
  public static readonly debounceDuration = 300;
  public static readonly typeFacetId = 'Type';
  public static readonly internalFacetId = 'Internal';
  public static readonly searchParams = {
    queryOffset: 0,
    itemsPerPage: 40,
  };

  @Input() public defaultTerm: string;
  @Input() public savedSearch: LearningResourceViewModel[];
  @Input() public savedManual: LearningResourceViewModel[];
  @Input() public exclusionList?: any[];
  @Input() public showHoldForLater = true;
  @Input() public inputsOnly = false;
  @Input() public isSubmitting: boolean;
  @Input() public resourceType: 'Pathway' | 'Target';
  @Input() public resourceOrgId?: number;
  @Input() public resourceId?: number;

  @Output() public addItems = new EventEmitter<
    ModalResults<LearningResourceViewModel>
  >();
  @Output() public addToBin = new EventEmitter<
    ModalResults<LearningResourceViewModel>
  >();
  @Output() public dismiss = new EventEmitter<Event>();
  @Output() public updateItemList = new EventEmitter<
    ModalResults<LearningResourceViewModel>
  >();

  public invalidateFilterCache: boolean;
  public isClientProvider: boolean;
  public isSearchOpen: boolean = true;
  public isSearching: boolean = true;
  public orgName: string = '';
  public resultCount: number = 0;
  public searchControl = new FormControl();
  public savedItems: LearningResourceViewModel[] = [];
  public searchFilters: SearchFilterFilter[] = [];
  public searchResults: LearningResourceViewModel[] = [];
  public searchTotal: number;
  public searchTerm: string;

  // Translated strings. Override in the extending component.
  public addItemsButtonText: string;
  public endorsedSrc: string;
  public searchCatalogText: string;
  public selectedText: string;
  public holdForLaterText: string;
  public closeSearchOnSelect: boolean = true;
  private hasOrgManagedContent: boolean;

  protected selectedFilters: SearchFilterFilter[] = [];

  constructor(
    protected authService: AuthService,
    protected cdr: ChangeDetectorRef,
    protected ldFlagsService: LDFlagsService,
    protected orgEndorsedService: OrgEndorsedService,
    protected searchService: SearchService,
    protected orgManagedService: OrgManagedContentService
  ) {
    super();
  }

  public get showOrgManagedNotification() {
    return !this.resourceOrgId && this.hasOrgManagedContent;
  }

  public ngOnChanges({ savedSearch }: SimpleChanges): void {
    if (
      savedSearch &&
      this.savedSearch &&
      this.savedItems !== this.savedSearch
    ) {
      this.savedItems = this.savedSearch;
      if (this.savedItems.length === 0) {
        this.openSearch();
      }
    }
  }

  public ngOnInit(): void {
    const authUser = this.authService.authUser;
    if (authUser) {
      const defaultOrganization = authUser.defaultOrgInfo;
      this.isClientProvider = defaultOrganization?.settings.isClientProvider;
      this.orgName = defaultOrganization?.name;
      this.endorsedSrc = this.orgEndorsedService.getEndorsedSrc(
        defaultOrganization?.organizationId
      );
    }

    this.searchTerm = this.defaultTerm;
    this.searchControl.setValue(this.searchTerm);
    this.searchSubmit(this.searchTerm);
    this.updateButtonText(this.savedManual);
    this.searchControl.valueChanges
      .pipe(
        debounceTime(AddBySearchBaseComponent.debounceDuration),
        filter((searchTerm: string) => searchTerm.length !== 1), // search term must be empty (for default search) or have at least two characters
        distinctUntilChanged(), // don't retrigger unless term has changed
        this.takeUntilDestroyed()
      )
      .subscribe((searchTerm) => this.searchSubmit(searchTerm)); // wrap the event handler instead of chaining; chaining would return an Observable
  }

  public openSearch() {
    if (!this.isSearchOpen) {
      this.isSearchOpen = true;
    }
  }

  public searchSubmit = (searchTerm: string) => {
    this.resetFilters();
    this.search(searchTerm);
  };

  public applyFilters(appliedFilters: SearchFilterFilter[]) {
    this.invalidateFilterCache = false;
    this.selectedFilters = appliedFilters;
    this.search(this.searchTerm);
  }

  public addToSavedList(item: LearningResourceViewModel, index: number): void {
    if (!this.isSearchOpen) {
      // PD-69647: If the search is closed, hitting enter/return should not select
      // the next item in the search list as this causes confusion for the user
      return;
    }

    if (this.closeSearchOnSelect) {
      this.isSearchOpen = false;
    }

    this.searchResults.splice(index, 1);

    let savedItemList: LearningResourceViewModel[];
    if (this.savedItems) {
      savedItemList = this.savedItems;
      savedItemList.push(item);
    } else {
      savedItemList = [item];
    }
    this.hasOrgManagedContent =
      this.orgManagedService.isContentsOrgManaged(savedItemList);
    this.updateButtonText(savedItemList);

    this.updateItemList.emit({
      savedItems: savedItemList,
      whichModal: AddBySearchBaseComponent.modalName,
    });
  }

  public removeFromSavedList = (item: LearningResourceViewModel): void => {
    let savedItemList: LearningResourceViewModel[];
    savedItemList = this.savedItems;
    if (!item) {
      // there's a problem. clear everything.
      savedItemList = [];
    } else {
      savedItemList = savedItemList.filter((i) => {
        return i.resourceId !== item.resourceId;
      });
    }
    this.hasOrgManagedContent =
      this.orgManagedService.isContentsOrgManaged(savedItemList);
    this.updateButtonText(savedItemList);
    this.updateItemList.emit({
      savedItems: savedItemList,
      whichModal: AddBySearchBaseComponent.modalName,
    });
  };

  public onHoldForLater(): void {
    this.addToBin.emit({
      savedItems: this.savedItems,
      whichModal: AddBySearchBaseComponent.modalName,
    });
  }

  public onAddItems(): void {
    this.addItems.emit({
      savedItems: this.savedItems,
      selectedSearch: this.savedSearch,
      whichModal: AddBySearchBaseComponent.modalName,
    });
  }

  /**
   * To be overridden in extending components.
   */
  protected resetFilters() {}

  /**
   * To be overridden in extending components.
   */
  protected search(_: string) {}

  /**
   * To be overridden in extending components.
   */
  protected updateButtonText(_: LearningResourceViewModel[]) {}
}
